import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, RefreshCw, Send, Webhook, Key, Eye, EyeOff, Copy, Users, Trash2, KeyRound, Plus } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

export default function Settings() {
  const { user: currentUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [webhook, setWebhook] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [hvThreshold, setHvThreshold] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [agentKey, setAgentKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [rotating, setRotating] = useState(false);
  // Users management
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [retesting, setRetesting] = useState(false);
  const [omieWh, setOmieWh] = useState(null);
  const [omieWhLogs, setOmieWhLogs] = useState([]);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/settings").then(r => r.data),
      api.get("/integrations/status").then(r => r.data).catch(() => null),
      api.get("/webhooks/config").then(r => r.data).catch(() => null),
      api.get("/webhooks/logs?limit=10").then(r => r.data).catch(() => []),
      api.get("/agent-key").then(r => r.data).catch(() => null),
      api.get("/auth/users").then(r => r.data).catch(() => []),
      api.get("/omie-webhook/config").then(r => r.data).catch(() => null),
      api.get("/omie-webhook/logs?limit=10").then(r => r.data).catch(() => []),
    ]).then(([s, st, wh, logs, ak, us, ow, owl]) => {
      setSettings(s);
      setStatus(st);
      setWebhook(wh);
      setWebhookUrl(wh?.target_url || "");
      setHvThreshold(wh?.high_value_threshold_brl != null ? String(wh.high_value_threshold_brl) : "");
      setWebhookLogs(logs);
      setAgentKey(ak);
      setUsers(us);
      setOmieWh(ow);
      setOmieWhLogs(owl);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Silently re-poll status every 60s if Omie or PDV is currently in error state.
  // Recovers the UI automatically once the transient outage clears.
  useEffect(() => {
    const omieErr = status && !status?.omie?.ok && settings?.omie_configured;
    const pdvErr = status && !status?.pdv?.ok && settings?.pdv_configured;
    if (!omieErr && !pdvErr) return;
    const t = setInterval(() => {
      api.get("/integrations/status").then(r => setStatus(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [status, settings]);

  const reloadUsers = () => api.get("/auth/users").then(r => setUsers(r.data)).catch(() => {});

  const createUser = async () => {
    const email = newUser.email.trim().toLowerCase();
    const password = newUser.password;
    if (!email || password.length < 8) {
      toast.error("E-mail e senha (min. 8 caracteres) são obrigatórios");
      return;
    }
    setCreatingUser(true);
    try {
      await api.post("/auth/users", { email, password, name: newUser.name.trim() });
      toast.success("Usuário criado");
      setNewUser({ name: "", email: "", password: "" });
      reloadUsers();
    } catch (e) {
      toast.error(`Falha: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCreatingUser(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remover o usuário ${u.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Usuário removido");
      reloadUsers();
    } catch (e) {
      toast.error(`Falha: ${e.response?.data?.detail || e.message}`);
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`Nova senha para ${u.email} (mín. 8 caracteres):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    try {
      await api.post(`/auth/users/${u.id}/reset-password`, { new_password: pw });
      toast.success(`Senha de ${u.email} atualizada`);
    } catch (e) {
      toast.error(`Falha: ${e.response?.data?.detail || e.message}`);
    }
  };

  const rotateKey = async () => {
    if (!window.confirm("Rotacionar a API key vai INVALIDAR a atual. Seu agente vai precisar ser atualizado. Continuar?")) return;
    setRotating(true);
    try {
      const r = await api.post("/agent-key/rotate");
      setAgentKey(prev => ({ ...prev, api_key: r.data.api_key, masked: "•".repeat(Math.max(0, r.data.api_key.length - 6)) + r.data.api_key.slice(-6) }));
      setShowKey(true);
      toast.success("Nova API key gerada");
    } catch (e) {
      toast.error("Erro ao rotacionar");
    } finally {
      setRotating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copiado`))
      .catch(() => toast.error("Não foi possível copiar"));
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    try {
      const r = await api.put("/webhooks/config", { url: webhookUrl });
      setWebhook(r.data);
      toast.success("URL do webhook salva");
    } catch (e) {
      toast.error("Erro ao salvar URL");
    } finally {
      setSavingWebhook(false);
    }
  };

  const saveThreshold = async () => {
    const v = parseFloat(hvThreshold);
    if (isNaN(v) || v < 0) {
      toast.error("Informe um valor válido em R$");
      return;
    }
    setSavingThreshold(true);
    try {
      const r = await api.put("/webhooks/config", { high_value_threshold_brl: v });
      setWebhook(r.data);
      setHvThreshold(String(r.data.high_value_threshold_brl));
      toast.success(`Limiar salvo: R$ ${v.toFixed(2)}`);
    } catch (e) {
      toast.error("Erro ao salvar limiar");
    } finally {
      setSavingThreshold(false);
    }
  };

  const testWebhook = async () => {
    setTestingWebhook(true);
    try {
      const r = await api.post("/webhooks/test", { url: webhookUrl || undefined });
      if (r.data.ok) {
        toast.success(`Webhook entregue (HTTP ${r.data.http_status}, ${r.data.attempts} tentativa)`);
      } else if (r.data.skipped) {
        toast.error(r.data.reason || "Webhook não configurado");
      } else {
        toast.error(`Falha: HTTP ${r.data.http_status || "—"}`);
      }
      // refresh logs
      const logs = await api.get("/webhooks/logs?limit=10").then(rr => rr.data);
      setWebhookLogs(logs);
    } catch (e) {
      toast.error(`Erro: ${e.message}`);
    } finally {
      setTestingWebhook(false);
    }
  };

  const doSync = async (kind, path, label) => {
    setSyncing(prev => ({ ...prev, [kind]: true }));
    try {
      const r = await api.post(path, {});
      if (kind === "excel") {
        const d = r.data || {};
        toast.success(
          `Excel importado: ${d.inventory_imported || 0} estoque · ${d.recipes_imported || 0} fichas · ${d.sales_imported || 0} vendas · ${d.purchase_planning_imported || 0} compras`
        );
      } else {
        toast.success(`${label}: ${r.data.count} registros`);
      }
    } catch (e) {
      toast.error(`${label} falhou: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [kind]: false }));
    }
  };

  if (loading) return <div className="font-body" style={{ color: "var(--ifl-muted)" }}>Carregando…</div>;

  const StatusBadge = ({ ok, label }) => (
    <span className="inline-flex items-center gap-2">
      {ok ? <CheckCircle2 size={16} strokeWidth={1.5} style={{ color: "var(--ifl-success)" }} /> : <AlertCircle size={16} strokeWidth={1.5} style={{ color: "var(--ifl-danger)" }} />}
      <span className="font-display text-xs uppercase tracking-widest">{label}</span>
    </span>
  );

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Configurações"
        title="INTEGRAÇÕES"
        accent="& sincronizações."
        description="Status das chaves Omie e PDV Legal, com sincronizações sob demanda."
        testId="settings-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="ifl-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Omie ERP</div>
              <div className="font-italic-serif text-xl mt-1">produção.</div>
            </div>
            <StatusBadge ok={settings.omie_configured && status?.omie?.ok} label={settings.omie_configured ? (status?.omie?.ok ? "Conectado" : "Erro") : "Sem chave"} />
          </div>
          <div className="font-body text-xs space-y-1 mb-4" style={{ color: "var(--ifl-muted)" }}>
            <div><strong style={{ color: "var(--ifl-text)" }}>Base:</strong> {settings.omie_base_url}</div>
            {status?.omie?.total_products !== undefined && (
              <div><strong style={{ color: "var(--ifl-text)" }}>Total produtos:</strong> {status.omie.total_products}</div>
            )}
            {status?.omie?.error && (
              <div className="flex items-start gap-2 mt-2" style={{ color: "var(--ifl-danger)" }}>
                <span>⚠</span>
                <div>
                  <div><strong>API do Omie instável agora.</strong> Tentamos 3x e recebemos 500. Isso é comum em horários de pico do Omie — não é problema das credenciais.</div>
                  <div className="font-mono text-[10px] mt-1 opacity-70">{String(status.omie.error)}</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              className="ifl-btn-outline"
              disabled={retesting}
              onClick={async () => {
                setRetesting(true);
                try {
                  const r = await api.get("/integrations/status");
                  setStatus(r.data);
                  if (r.data?.omie?.ok) toast.success("Omie respondeu OK");
                  else toast.error("Omie ainda instável — tente em 1 min");
                } catch (e) { toast.error("Falha ao testar"); }
                finally { setRetesting(false); }
              }}
              data-testid="retest-omie-btn"
            >
              <RefreshCw size={12} strokeWidth={2} className={retesting ? "ifl-pulse" : ""} />Testar agora
            </button>
            <button className="ifl-btn-outline" disabled={syncing.products} onClick={() => doSync("products", "/sync/omie/products", "Produtos")} data-testid="sync-omie-products">
              <RefreshCw size={12} strokeWidth={2} className={syncing.products ? "ifl-pulse" : ""} />Produtos
            </button>
            <button className="ifl-btn-outline" disabled={syncing.inventory} onClick={() => doSync("inventory", "/sync/omie/inventory", "Estoque")} data-testid="sync-omie-inventory">
              <RefreshCw size={12} strokeWidth={2} className={syncing.inventory ? "ifl-pulse" : ""} />Estoque
            </button>
            <button className="ifl-btn-outline" disabled={syncing.recipes} onClick={() => doSync("recipes", "/sync/omie/recipes", "Fichas")} data-testid="sync-omie-recipes">
              <RefreshCw size={12} strokeWidth={2} className={syncing.recipes ? "ifl-pulse" : ""} />Fichas
            </button>
            <button className="ifl-btn-outline" disabled={syncing.purchases} onClick={() => doSync("purchases", "/sync/omie/purchases", "NFs Entrada")} data-testid="sync-omie-purchases">
              <RefreshCw size={12} strokeWidth={2} className={syncing.purchases ? "ifl-pulse" : ""} />NFs Entrada
            </button>
            <button className="ifl-btn-outline" disabled={syncing.revenue} onClick={() => doSync("revenue", "/sync/omie/revenue", "Receita (OS + NF-e + Pedidos)")} data-testid="sync-omie-revenue">
              <RefreshCw size={12} strokeWidth={2} className={syncing.revenue ? "ifl-pulse" : ""} />Eventos / NF-e (saída)
            </button>
          </div>
        </div>

        <div className="ifl-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">PDV Legal (TabletCloud)</div>
              <div className="font-italic-serif text-xl mt-1">produção.</div>
            </div>
            <StatusBadge ok={settings.pdv_configured && status?.pdv?.ok} label={settings.pdv_configured ? (status?.pdv?.ok ? "Conectado" : "Erro") : "Sem chave"} />
          </div>
          <div className="font-body text-xs space-y-1 mb-4" style={{ color: "var(--ifl-muted)" }}>
            <div><strong style={{ color: "var(--ifl-text)" }}>Base:</strong> {settings.pdv_base_url}</div>
            <div><strong style={{ color: "var(--ifl-text)" }}>Tenant:</strong> 28212 · Loja 34042 · GRUPO INDIE FOOD LAB</div>
            {status?.pdv?.error && <div style={{ color: "var(--ifl-danger)" }}>{String(status.pdv.error)}</div>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="ifl-btn-outline" disabled={syncing.pdv_today} onClick={() => doSync("pdv_today", "/sync/quick", "Vendas de hoje")} data-testid="sync-pdv-today">
              <RefreshCw size={12} strokeWidth={2} className={syncing.pdv_today ? "ifl-pulse" : ""} />Hoje
            </button>
            <button className="ifl-btn-outline" disabled={syncing.pdv_gaps} onClick={() => doSync("pdv_gaps", "/sync/pdv/fill-gaps?days=60", "Preencher gaps (60d)")} data-testid="sync-pdv-gaps">
              <RefreshCw size={12} strokeWidth={2} className={syncing.pdv_gaps ? "ifl-pulse" : ""} />Gaps (últ. 60 dias)
            </button>
            <button
              className="ifl-btn"
              disabled={syncing.pdv_history}
              onClick={async () => {
                const start = window.prompt("Sincronizar histórico do PDV a partir de qual data? (formato AAAA-MM-DD)", "2025-12-01");
                if (!start) return;
                if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) { toast.error("Formato inválido. Use AAAA-MM-DD"); return; }
                setSyncing(s => ({ ...s, pdv_history: true }));
                const t = toast.loading(`Sincronizando histórico desde ${start}…`);
                try {
                  const r = await api.post("/sync/pdv/history", { start_date: start });
                  const d = r.data;
                  toast.success(`Histórico: ${d.days_fetched} dias buscados, ${d.days_with_data} com dados, ${d.inserted} cupons novos`, { id: t });
                } catch (e) {
                  toast.error(`Falha: ${e.response?.data?.detail || e.message}`, { id: t });
                } finally {
                  setSyncing(s => ({ ...s, pdv_history: false }));
                }
              }}
              data-testid="sync-pdv-history"
            >
              <RefreshCw size={12} strokeWidth={2} className={syncing.pdv_history ? "ifl-pulse" : ""} />Histórico completo
            </button>
          </div>
          <div className="mt-4 text-xs font-body" style={{ color: "var(--ifl-muted)" }}>
            <strong style={{ color: "var(--ifl-text)" }}>Como funciona:</strong> Hoje + ontem são sincronizados de hora em hora pelo scheduler. Dias já fechados ficam armazenados no banco e <em>não</em> são re-consultados na API do PDV. Use "Histórico completo" pra puxar de uma vez todo o passado.
          </div>
        </div>
      </div>

      <div className="ifl-card p-6">
        <div className="label-caps mb-2">Importar planilhas locais</div>
        <div className="font-italic-serif text-xl mb-3">para começar com dados reais.</div>
        <p className="font-body text-sm mb-4" style={{ color: "var(--ifl-muted)" }}>
          Reimporta as planilhas anexadas (Posição Estoque, Fichas, Resumo Geral Vendas e Planilha Compras). Útil enquanto os endpoints específicos do PDV ainda não estão liberados.
        </p>
        <button className="ifl-btn" onClick={() => doSync("excel", "/seed/excel", "Importação Excel")} disabled={syncing.excel} data-testid="seed-excel-btn">
          <RefreshCw size={14} strokeWidth={2} className={syncing.excel ? "ifl-pulse" : ""} />
          {syncing.excel ? "Importando…" : "Reimportar planilhas"}
        </button>
      </div>

      {/* Agent API (inbound) */}
      <div className="ifl-card p-6 mt-6" data-testid="agent-api-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Key size={14} strokeWidth={1.5} />
              <div className="label-caps">API do Agente (Leitura)</div>
            </div>
            <div className="font-italic-serif text-xl mt-1">tudo do sistema, ao alcance do seu agente.</div>
          </div>
          <button
            className="ifl-btn-outline"
            onClick={rotateKey}
            disabled={rotating}
            data-testid="rotate-key-btn"
          >
            <RefreshCw size={12} strokeWidth={2} className={rotating ? "ifl-pulse" : ""} />
            {rotating ? "Rotacionando…" : "Rotacionar"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="label-caps mb-1">API key</div>
            <div className="flex gap-2">
              <input
                value={agentKey ? (showKey ? agentKey.api_key : agentKey.masked) : ""}
                readOnly
                className="ifl-input flex-1 font-mono text-xs"
                data-testid="agent-key-input"
              />
              <button
                className="ifl-btn-outline"
                onClick={() => setShowKey(s => !s)}
                title={showKey ? "Ocultar" : "Mostrar"}
                data-testid="toggle-key-visibility"
              >
                {showKey ? <EyeOff size={14} strokeWidth={1.5} /> : <Eye size={14} strokeWidth={1.5} />}
              </button>
              <button
                className="ifl-btn-outline"
                onClick={() => agentKey && copyToClipboard(agentKey.api_key, "API key")}
                title="Copiar"
                data-testid="copy-key-btn"
              >
                <Copy size={14} strokeWidth={1.5} />
              </button>
            </div>
            <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>
              Use no header: <code style={{ color: "var(--ifl-text)" }}>Authorization: Bearer &lt;api_key&gt;</code>
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Base URL</div>
            <input
              value={`${window.location.origin}/api/agent`}
              readOnly
              className="ifl-input w-full font-mono text-xs"
              data-testid="agent-base-url"
              onClick={(e) => e.target.select()}
            />
            <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>
              Acesse <code style={{ color: "var(--ifl-text)" }}>/api/agent/me</code> para validar a autenticação.
            </div>
          </div>
        </div>

        <div className="ifl-card p-4 mb-4" style={{ background: "var(--ifl-bg)" }}>
          <div className="label-caps mb-2">Endpoints disponíveis (todos GET)</div>
          <table className="w-full text-xs font-body">
            <tbody>
              {[
                ["/me", "Sanity check + contagem de registros"],
                ["/dashboard", "KPIs agregados (receita, estoque, CMV)"],
                ["/recipes?category=&with_ingredients_only=&limit=", "Lista todas as fichas"],
                ["/recipes/{id_ou_codigo}", "Ficha específica por UUID ou código (ex: 203)"],
                ["/recipes/search?q=...", "Busca fichas por nome ou código"],
                ["/ingredients/{code}/where-used", "Fichas que usam um insumo (impacto de preço)"],
                ["/inventory?search=&only_critical=", "Posição de estoque"],
                ["/inventory/{code}", "Item específico do estoque"],
                ["/sales?start_date=&end_date=", "Cupons PDV no período"],
                ["/sales/summary?start_date=&end_date=", "Receita agregada por dia"],
                ["/purchases", "Notas fiscais de entrada (Omie)"],
                ["/purchase-planning", "Planejamento semanal de compras"],
                ["/reconciliation", "Variância de estoque (perdas potenciais)"],
              ].map(([path, desc]) => (
                <tr key={path} className="border-b last:border-b-0" style={{ borderColor: "rgba(229, 225, 216, 0.6)" }}>
                  <td className="py-2 pr-3 font-mono whitespace-nowrap" style={{ color: "var(--ifl-text)" }}>{path}</td>
                  <td className="py-2" style={{ color: "var(--ifl-muted)" }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ifl-card p-4" style={{ background: "var(--ifl-bg)" }}>
          <div className="label-caps mb-2">Exemplo de chamada</div>
          <pre className="text-xs font-mono p-3 rounded-lg overflow-x-auto" style={{ background: "#fff", border: "1px solid var(--ifl-border)", color: "var(--ifl-text)" }}>
{`curl -H "Authorization: Bearer ${agentKey?.api_key || "<api_key>"}" \\
  ${window.location.origin}/api/agent/recipes/203

# Buscar pratos que usam o insumo de código 14 (alho)
curl -H "Authorization: Bearer ${agentKey?.api_key || "<api_key>"}" \\
  ${window.location.origin}/api/agent/ingredients/14/where-used`}
          </pre>
        </div>
      </div>

      {/* Webhooks */}
      <div className="ifl-card p-6 mt-6" data-testid="webhook-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Webhook size={14} strokeWidth={1.5} />
              <div className="label-caps">Webhooks</div>
            </div>
            <div className="font-italic-serif text-xl mt-1">avisa seu agente quando uma ficha nasce.</div>
          </div>
          <button className="ifl-btn-outline" onClick={testWebhook} disabled={testingWebhook} data-testid="webhook-test-btn">
            <Send size={12} strokeWidth={2} className={testingWebhook ? "ifl-pulse" : ""} />
            {testingWebhook ? "Enviando…" : "Enviar teste"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="label-caps mb-1">URL do agente externo</div>
            <div className="flex gap-2">
              <input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://meu-agente.com/webhooks/ficha"
                className="ifl-input flex-1"
                data-testid="webhook-url-input"
              />
              <button className="ifl-btn" onClick={saveWebhook} disabled={savingWebhook} data-testid="webhook-save-btn">
                {savingWebhook ? "Salvando…" : "Salvar"}
              </button>
            </div>
            <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>
              Eventos: <code style={{ color: "var(--ifl-text)" }}>recipe.created</code>, <code style={{ color: "var(--ifl-text)" }}>recipe.updated</code>, <code style={{ color: "var(--ifl-text)" }}>customer.high_value_purchase</code>
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Segredo (mascarado)</div>
            <input
              value={webhook?.secret_masked || ""}
              readOnly
              className="ifl-input w-full font-mono text-xs"
              data-testid="webhook-secret-masked"
            />
            <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>
              O segredo completo está em <code style={{ color: "var(--ifl-text)" }}>backend/.env → WEBHOOK_SECRET</code>. Compartilhe com seu agente para validar a assinatura.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="label-caps mb-1">Limiar de compra alta (R$)</div>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                value={hvThreshold}
                onChange={(e) => setHvThreshold(e.target.value)}
                placeholder="200.00"
                className="ifl-input flex-1"
                data-testid="hv-threshold-input"
              />
              <button className="ifl-btn" onClick={saveThreshold} disabled={savingThreshold} data-testid="hv-threshold-save-btn">
                {savingThreshold ? "Salvando…" : "Salvar"}
              </button>
            </div>
            <div className="font-body text-xs mt-2" style={{ color: "var(--ifl-muted)" }}>
              Cupons novos com cliente identificado e valor ≥ este limiar disparam <code style={{ color: "var(--ifl-text)" }}>customer.high_value_purchase</code>.
            </div>
          </div>
        </div>

        <div className="ifl-card p-4 mb-6" style={{ background: "var(--ifl-bg)" }}>
          <div className="label-caps mb-2">Headers enviados</div>
          <ul className="text-xs font-body space-y-1" style={{ color: "var(--ifl-text)" }}>
            <li><code>X-Webhook-Event</code> — nome do evento (ex: recipe.created)</li>
            <li><code>X-Webhook-Id</code> — UUID único por entrega (use para idempotência)</li>
            <li><code>X-Webhook-Timestamp</code> — unix epoch em segundos (rejeite se &gt; 5 min)</li>
            <li><code>X-Webhook-Signature</code> — <code>sha256=&lt;hmac_hex&gt;</code>, HMAC-SHA256 de <code>{`{timestamp}.{raw_body}`}</code></li>
            <li><code>X-Webhook-Source</code> — sempre <code>indie-food-lab-custos</code></li>
          </ul>
          <div className="label-caps mt-4 mb-2">Payload (JSON)</div>
          <pre className="text-xs font-mono p-3 rounded-lg overflow-x-auto" style={{ background: "#fff", border: "1px solid var(--ifl-border)", color: "var(--ifl-text)" }}>
{`{
  "id": "<uuid>",
  "event": "recipe.created",
  "ts": "<epoch>",
  "source": "indie-food-lab-custos",
  "data": {
    "id": "...", "code": "203", "name": "Alho Conf",
    "yield_quantity": 1, "yield_unit": "UN",
    "sale_price": 0, "total_cost": 0.004, "cmv_pct": 0,
    "ingredients": [
      { "code": "14", "name": "alho", "quantity": 0.075,
        "unit": "G", "unit_cost": 0.0306, "line_cost": 0.0023 }
    ],
    "notes": "..."
  }
}`}
          </pre>
        </div>

        <div className="label-caps mb-2">Últimas entregas</div>
        <div className="ifl-card overflow-hidden">
          <table className="ifl-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Evento</th>
                <th>Destino</th>
                <th>Status</th>
                <th className="text-right">Tentativas</th>
              </tr>
            </thead>
            <tbody>
              {webhookLogs.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--ifl-muted)" }}>Nenhuma entrega registrada.</td></tr>
              )}
              {webhookLogs.map((l) => {
                const ok = l.status === "delivered";
                const skipped = l.status === "skipped";
                return (
                  <tr key={l.id}>
                    <td className="font-display text-xs">{new Date((l.ts || 0) * 1000).toLocaleString("pt-BR")}</td>
                    <td className="font-display text-xs">{l.event}</td>
                    <td className="text-xs truncate" style={{ maxWidth: 240 }}>{l.target_url || "—"}</td>
                    <td>
                      <span className="inline-flex items-center gap-2">
                        <span className="ifl-status-dot" style={{ background: ok ? "var(--ifl-success)" : skipped ? "var(--ifl-mustard)" : "var(--ifl-danger)" }} />
                        <span className="font-display text-xs uppercase tracking-widest">
                          {ok ? `OK ${l.http_status || ""}` : skipped ? "ignorado" : `falha ${l.http_status || ""}`}
                        </span>
                      </span>
                    </td>
                    <td className="text-right font-display">{l.attempts || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Omie Webhook (incoming push) */}
      {omieWh && (
        <div className="ifl-card p-6 mt-6" data-testid="omie-webhook-section">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Webhook size={14} strokeWidth={1.5} />
                <div className="label-caps">Webhook do Omie (entrada)</div>
              </div>
              <div className="font-italic-serif text-xl mt-1">sync em tempo real.</div>
              <div className="font-body text-sm mt-2" style={{ color: "var(--ifl-muted)" }}>
                Quando o Omie cria ou altera um lançamento (conta a pagar, NF-e, OS, pedido), ele chama o endpoint abaixo e a gente sincroniza na hora — sem esperar a próxima rodada agendada.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-2">
              <div className="label-caps mb-1">Endpoint URL (cole no Omie)</div>
              <div className="flex gap-2">
                <input value={omieWh.endpoint_url} readOnly className="ifl-input flex-1 font-mono text-xs" data-testid="omie-wh-endpoint" />
                <button
                  className="ifl-btn-outline"
                  onClick={() => { navigator.clipboard.writeText(omieWh.endpoint_url); setCopied(true); toast.success("URL copiada"); setTimeout(() => setCopied(false), 1500); }}
                  data-testid="copy-omie-wh-url"
                >
                  <Copy size={12} strokeWidth={2} />{copied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
            <div>
              <div className="label-caps mb-1">Token (header `Authorization`)</div>
              <div className="font-mono text-xs px-3 py-2 rounded border" style={{ borderColor: "var(--ifl-border)", background: "rgba(15,27,45,0.03)" }} data-testid="omie-wh-token-mask">
                Bearer {omieWh.token_masked || "—"}
              </div>
              <div className="font-body text-[10px] mt-1" style={{ color: "var(--ifl-muted)" }}>
                Token completo em <code>backend/.env → OMIE_WEBHOOK_TOKEN</code>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="label-caps mb-2">Eventos recomendados</div>
            <div className="flex flex-wrap gap-1.5">
              {(omieWh.recommended_events || []).map(e => (
                <span key={e} className="font-mono text-[10px] px-2 py-1 rounded border" style={{ borderColor: "var(--ifl-border)", color: "var(--ifl-text)" }}>
                  {e}
                </span>
              ))}
            </div>
          </div>

          <div className="font-body text-xs p-3 rounded border" style={{ borderColor: "var(--ifl-border)", background: "rgba(196,138,46,0.06)", color: "var(--ifl-text)" }}>
            <strong>Como configurar:</strong> {omieWh.instructions}
          </div>

          {/* Recent logs */}
          {omieWhLogs && omieWhLogs.length > 0 && (
            <div className="mt-5">
              <div className="label-caps mb-2">Eventos recentes</div>
              <div className="ifl-card overflow-hidden">
                <table className="ifl-table">
                  <thead><tr><th>Recebido em</th><th>Tópico</th><th>Tipo</th><th>Status</th></tr></thead>
                  <tbody>
                    {omieWhLogs.map((l) => (
                      <tr key={l.message_id}>
                        <td className="font-mono text-xs">{new Date(l.received_at).toLocaleString("pt-BR")}</td>
                        <td className="font-mono text-xs">{l.topic}</td>
                        <td className="font-display text-xs uppercase tracking-wider" style={{ color: "var(--ifl-muted)" }}>{l.kind || "—"}</td>
                        <td>
                          <span className="font-display text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border" style={{
                            color: l.status === "ok" ? "var(--ifl-success)" : l.status === "error" ? "var(--ifl-danger)" : "var(--ifl-mustard)",
                            borderColor: l.status === "ok" ? "var(--ifl-success)" : l.status === "error" ? "var(--ifl-danger)" : "var(--ifl-mustard)",
                          }}>{l.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users management */}
      <div className="ifl-card p-6 mt-6" data-testid="users-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Users size={14} strokeWidth={1.5} />
              <div className="label-caps">Usuários</div>
            </div>
            <div className="font-italic-serif text-xl mt-1">quem tem acesso ao painel.</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6 items-end">
          <div className="lg:col-span-1">
            <div className="label-caps mb-1">Nome</div>
            <input
              value={newUser.name}
              onChange={(e) => setNewUser(u => ({ ...u, name: e.target.value }))}
              placeholder="Ex: Bruno"
              className="ifl-input w-full"
              data-testid="new-user-name"
            />
          </div>
          <div className="lg:col-span-1">
            <div className="label-caps mb-1">E-mail</div>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser(u => ({ ...u, email: e.target.value }))}
              placeholder="pessoa@indiefoodlab.com.br"
              className="ifl-input w-full"
              data-testid="new-user-email"
            />
          </div>
          <div className="lg:col-span-1">
            <div className="label-caps mb-1">Senha (mín. 8)</div>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser(u => ({ ...u, password: e.target.value }))}
              placeholder="••••••••"
              className="ifl-input w-full"
              data-testid="new-user-password"
            />
          </div>
          <div>
            <button
              onClick={createUser}
              disabled={creatingUser}
              className="ifl-btn w-full justify-center"
              data-testid="create-user-btn"
            >
              <Plus size={14} strokeWidth={2} />
              {creatingUser ? "Criando…" : "Adicionar"}
            </button>
          </div>
        </div>

        <div className="ifl-card overflow-hidden">
          <table className="ifl-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Criado em</th>
                <th>Último login</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8" style={{ color: "var(--ifl-muted)" }}>Nenhum usuário cadastrado.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="text-sm">{u.name || "—"} {currentUser?.id === u.id && <span className="font-body text-xs ml-2" style={{ color: "var(--ifl-muted)" }}>(você)</span>}</td>
                  <td className="text-sm font-mono">{u.email}</td>
                  <td className="font-display text-xs" style={{ color: "var(--ifl-muted)" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="font-display text-xs" style={{ color: "var(--ifl-muted)" }}>{u.last_login ? new Date(u.last_login).toLocaleString("pt-BR") : "nunca"}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => resetPassword(u)}
                        className="ifl-btn-outline"
                        style={{ padding: "6px 10px" }}
                        title="Resetar senha"
                        data-testid={`reset-pw-${u.id}`}
                      >
                        <KeyRound size={12} strokeWidth={2} />
                        Senha
                      </button>
                      {currentUser?.id !== u.id && (
                        <button
                          onClick={() => removeUser(u)}
                          className="ifl-btn-outline"
                          style={{ padding: "6px 10px", color: "var(--ifl-danger)", borderColor: "var(--ifl-danger)" }}
                          title="Remover usuário"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={12} strokeWidth={2} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
